import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, Alert, List, Typography, Space, Spin, Empty, Button } from 'antd';
import { ScanOutlined, CheckCircleOutlined, CloseCircleOutlined, HistoryOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as actions from "store/actions/AuthActions";

const { Text } = Typography;

const BarcodeScanner = ({ visible, onClose }) => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [scanValue, setScanValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [recentScans, setRecentScans] = useState([]);

    // Focus input when modal opens
    useEffect(() => {
        if (visible && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [visible]);

    // Load recent scans from localStorage
    useEffect(() => {
        if (visible) {
            const stored = localStorage.getItem('recentAssetScans');
            if (stored) {
                try {
                    setRecentScans(JSON.parse(stored));
                } catch (e) {
                    setRecentScans([]);
                }
            }
        }
    }, [visible]);

    // Save recent scan to localStorage
    const saveRecentScan = (barcode, asset, success) => {
        const scan = {
            barcode,
            asset_id: asset?.id,
            asset_name: asset?.name,
            asset_number: asset?.asset_number,
            success,
            timestamp: new Date().toISOString()
        };

        const updated = [scan, ...recentScans.filter(s => s.barcode !== barcode)].slice(0, 10);
        setRecentScans(updated);
        localStorage.setItem('recentAssetScans', JSON.stringify(updated));
    };

    const handleScan = async (value) => {
        const barcode = value.trim();
        if (!barcode) return;

        setLoading(true);
        setResult(null);

        try {
            // Search for asset by barcode
            const response = await actions.loadAssets(1, barcode, 10, null);

            if (response && response.status === 200) {
                const assets = response.data.results || [];

                // Find exact barcode match
                const exactMatch = assets.find(a =>
                    a.barcode === barcode ||
                    a.asset_number === barcode
                );

                if (exactMatch) {
                    setResult({
                        success: true,
                        asset: exactMatch,
                        message: 'Asset found!'
                    });
                    saveRecentScan(barcode, exactMatch, true);

                    // Auto-navigate after short delay
                    setTimeout(() => {
                        onClose();
                        navigate(`/assets/edit/${exactMatch.id}`);
                    }, 800);
                } else if (assets.length > 0) {
                    // Partial match found
                    setResult({
                        success: true,
                        asset: assets[0],
                        message: 'Asset found (partial match)',
                        assets: assets
                    });
                    saveRecentScan(barcode, assets[0], true);
                } else {
                    setResult({
                        success: false,
                        message: 'No asset found with this barcode'
                    });
                    saveRecentScan(barcode, null, false);
                }
            } else {
                setResult({
                    success: false,
                    message: 'Failed to search for asset'
                });
            }
        } catch (error) {
            console.error('Scan error:', error);
            setResult({
                success: false,
                message: 'Error searching for asset'
            });
            saveRecentScan(barcode, null, false);
        }

        setLoading(false);
        setScanValue('');

        // Refocus input for next scan
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleInputChange = (e) => {
        setScanValue(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleScan(scanValue);
        }
    };

    const handleRecentClick = (scan) => {
        if (scan.success && scan.asset_id) {
            onClose();
            navigate(`/assets/edit/${scan.asset_id}`);
        }
    };

    const clearRecentScans = () => {
        setRecentScans([]);
        localStorage.removeItem('recentAssetScans');
    };

    const handleClose = () => {
        setScanValue('');
        setResult(null);
        onClose();
    };

    return (
        <Modal
            title={
                <Space>
                    <ScanOutlined />
                    <span>Barcode Scanner</span>
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={500}
            destroyOnClose
        >
            <Alert
                message="Scan a barcode to find and edit the asset"
                description="Position your barcode scanner and scan. The input field below will capture the barcode automatically."
                type="info"
                showIcon
                style={{ marginBottom: 20 }}
            />

            <div style={{ marginBottom: 20 }}>
                <Input
                    ref={inputRef}
                    size="large"
                    placeholder="Scan barcode here or type manually..."
                    prefix={<ScanOutlined />}
                    value={scanValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    style={{ fontSize: 18 }}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Press Enter to search manually
                </Text>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 10 }}>
                        <Text>Searching for asset...</Text>
                    </div>
                </div>
            )}

            {result && !loading && (
                <Alert
                    message={result.success ? "Asset Found" : "Not Found"}
                    description={
                        result.success ? (
                            <div>
                                <Text strong>{result.asset.name}</Text>
                                <br />
                                <Text type="secondary">
                                    {result.asset.asset_number} | {result.asset.barcode}
                                </Text>
                                {result.assets && result.assets.length > 1 && (
                                    <div style={{ marginTop: 10 }}>
                                        <Text type="secondary">
                                            Found {result.assets.length} matching assets. Navigating to first match...
                                        </Text>
                                    </div>
                                )}
                            </div>
                        ) : (
                            result.message
                        )
                    }
                    type={result.success ? "success" : "error"}
                    showIcon
                    icon={result.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{ marginBottom: 20 }}
                    action={
                        result.success && result.assets && result.assets.length > 1 ? (
                            <Button
                                size="small"
                                onClick={() => {
                                    onClose();
                                    navigate(`/assets/edit/${result.asset.id}`);
                                }}
                            >
                                Go to Asset
                            </Button>
                        ) : null
                    }
                />
            )}

            {recentScans.length > 0 && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <Text strong>
                            <HistoryOutlined /> Recent Scans
                        </Text>
                        <Button type="link" size="small" onClick={clearRecentScans}>
                            Clear
                        </Button>
                    </div>
                    <List
                        size="small"
                        bordered
                        dataSource={recentScans}
                        renderItem={(scan) => (
                            <List.Item
                                style={{
                                    cursor: scan.success ? 'pointer' : 'default',
                                    opacity: scan.success ? 1 : 0.6
                                }}
                                onClick={() => handleRecentClick(scan)}
                            >
                                <Space>
                                    {scan.success ? (
                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    ) : (
                                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                    )}
                                    <div>
                                        <Text code>{scan.barcode}</Text>
                                        {scan.success && (
                                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                                {scan.asset_name}
                                            </Text>
                                        )}
                                    </div>
                                </Space>
                            </List.Item>
                        )}
                        style={{ maxHeight: 200, overflowY: 'auto' }}
                    />
                </div>
            )}

            {recentScans.length === 0 && !result && !loading && (
                <Empty
                    description="No recent scans"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            )}
        </Modal>
    );
};

export default BarcodeScanner;
